// @flow

import React from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import { setUIState } from 'redux/modules/ui'
import { actions as RoomActions } from 'redux/modules/room'
import { actions as UserActions } from 'redux/modules/remote/user'
import { actions as MasterActions } from 'redux/modules/remote/master'
import UserContactModal from 'components/organisms/UserContactModal'
import { type User as UserType } from 'types/user'
import { type Master as MasterType } from 'types/v1/master'

import UserContactModalUseCase, {
  type UserContactModalUseCaseType,
} from './usecase'

type Props = {
  fromType: 'reservation' | 'inquiry',
  currentUser: {
    contact: Object,
    phone: Object,
  },
  errors: {
    messages: string,
  },
  roomId: string,
  usecase: UserContactModalUseCaseType,
  ui: {
    isAlreadySavedContact: boolean,
    phoneNumber: string,
    verifyCode: string,
    sentPhoneNumber: string,
    sentSms: string,
    contact: {
      id: string,
      familyName: string,
      givenName: string,
      corpName: string,
      department: string,
      phone: string,
    },
  },
  user: {
    user: UserType,
    sendPhoneNumberError: {
      message: string,
    },
    sendError: {
      message: string,
    },
    verifyError: {
      message: string,
    },
  },
  master: {
    largeAreas: Array<MasterType>,
  },
  config: {
    SPM_GOOGLE_API_KEY?: ?string,
  },
}

class UserContactModalContainer extends React.Component<Props, *> {
  static errorText(resource, field, errors) {
    let errorText = ''
    if (errors) {
      for (let i = 0; i < errors.length; i++) {
        if (errors[i].resource == resource && errors[i].field == field) {
          errorText += errors[i].message
        }
      }
    }
    return errorText
  }

  componentDidMount() {
    const { usecase } = this.props
    usecase.initialize()
  }

  canSaveContact = () => {
    const { ui } = this.props
    const contact = ui.contact || {}

    return (
      contact &&
      typeof contact.familyName === 'string' &&
      typeof contact.givenName === 'string'
    )
  }

  convertMasterDataToSelectProps = (dataList: Array<MasterDataType>) => {
    return dataList.map(data => ({
      label: data.name_text,
      value: data.name,
    }))
  }

  generateModalProps = () => {
    const { fromType, usecase, ui, user, master } = this.props
    const contact = ui.contact || {}
    const fetchedUser = user.user || {}
    const hasContact = contact.id !== undefined
    const hasConfirmedPhone =
      fetchedUser.has_confirmed_phone || ui.hasConfirmedPhone || false
    const canSaveContact = this.canSaveContact()
    const isSubmitButtonDisabled = !hasConfirmedPhone || !canSaveContact

    const fromText =
      fromType === 'reservation'
        ? i18n.t('page.shared.reservationRequest')
        : i18n.t('page.shared.inquiry')
    const submitButtonText = hasContact
      ? ''
      : i18n.t('userContact.save', { fromText })

    return {
      // モーダル制御
      user: fetchedUser,
      hasConfirmedPhone,
      title: ui.isAlreadySavedContact
        ? i18n.t('phoneVerification.title')
        : i18n.t('userContact.title'),
      fromText,
      submitButtonText,
      isSubmitButtonDisabled,
      isAlreadySavedContact: ui.isAlreadySavedContact,
      sentPhoneNumber: ui.sentPhoneNumber,
      sentSms: ui.sentSms,
      sendError: user.sendPhoneNumberError,
      verifyError: user.verifyCodeError,
      onClickClose: (e: Event) => {
        e.preventDefault()
      },
      // マスタ系
      largeAreas: this.convertMasterDataToSelectProps(master.largeAreas),
      // フィールド系
      familyName: {
        value: contact.familyName,
        onChange: (e: SyntheticInputEvent<*>) =>
          usecase.handleChangeUIContactField(e, 'familyName'),
        error: UserContactModalContainer.errorText(
          'User',
          'contact.family_name',
          this.props.errors,
        ),
      },
      givenName: {
        value: contact.givenName,
        onChange: (e: SyntheticInputEvent<*>) =>
          usecase.handleChangeUIContactField(e, 'givenName'),
        error: UserContactModalContainer.errorText(
          'User',
          'contact.given_name',
          this.props.errors,
        ),
      },
      corpName: {
        value: contact.corpName,
        onChange: (e: SyntheticInputEvent<*>) =>
          usecase.handleChangeUIContactField(e, 'corpName'),
        error: UserContactModalContainer.errorText(
          'User',
          'contact.corp_name',
          this.props.errors,
        ),
      },
      department: {
        value: contact.department,
        onChange: (e: SyntheticInputEvent<*>) =>
          usecase.handleChangeUIContactField(e, 'department'),
        error: UserContactModalContainer.errorText(
          'User',
          'contact.department',
          this.props.errors,
        ),
      },
      phone: {
        value: ui.phoneNumber,
        onChange: (e: Event) => usecase.handleChangeUIField(e, 'phoneNumber'),
        hasConfirmed: hasConfirmedPhone,
      },
      code: {
        value: ui.verifyCode,
        onChange: (e: Event) => usecase.handleChangeUIField(e, 'verifyCode'),
      },
      onClickSendPhoneNumber: usecase.sendSms,
      onClickBackToPhoneNumber: usecase.backToPhoneNumber,
      onClickMakeCall: usecase.makeCall,
      onClickSendVerifyCode: usecase.verifyCode,
      onClickToInquiry: usecase.toInquiry,
    }
  }

  render() {
    const { usecase } = this.props

    const userContactModalProps = this.generateModalProps()

    return (
      <UserContactModal
        {...userContactModalProps}
        onClickClose={usecase.closeModal}
      />
    )
  }
}

function mapStateToProps(state) {
  return {
    ui: state.ui,
    user: state.user,
    master: state.master,
    config: state.config,
  }
}

function mapDispatchToProps(dispatch: Dispatch<*>) {
  const bindActions = {
    setUIState,
    ...RoomActions,
    ...MasterActions,
    ...UserActions,
  }
  return { actions: bindActionCreators(bindActions, dispatch) }
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(UserContactModalUseCase(UserContactModalContainer)),
)



// WEBPACK FOOTER //
// ./src/containers/organisms/UserContactModal/index.jsx